import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { useMemberReportGenerator } from '../../hooks/useMemberReportGenerator';
import { useAccountReportGenerator } from '../../hooks/useAccountReportGenerator'; 
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import type { Account, Member, FinancialRecord } from '../../models';

const ReportsPage: React.FC = () => {
    const { handleGenerateAllMembersReport, loadingAllMembers, setLoadingAllMembers } = useMemberReportGenerator();
    const { loadingFinancialRecords,
        setLoadingFinancialRecords,
        handleGenerateAccountFinancialReport
    } = useAccountReportGenerator();

    const [loadingAccountMembers, setLoadingAccountMembers] = useState(false);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [selectedAccountName, setSelectedAccountName] = useState<string>('');
    const { showToast } = useToast();
    const { user } = useAuth();

        const [allMembersReportFormat, setAllMembersReportFormat] = useState<'csv' | 'pdf'>('csv'); // State for All Members Report format
    const [accountMembersReportFormat, setAccountMembersReportFormat] = useState<'csv' | 'pdf'>('csv'); // State for Account Members Report format
    const [financialReportFormat, setFinancialReportFormat] = useState<'csv' | 'pdf'>('csv'); // State for Financial Report format

    const isSuperAdmin = user?.role === 'super-admin';

    // Effect to fetch accounts when component mounts (only for super-admin)
    useEffect(() => {
        if (isSuperAdmin) {
            const fetchAccounts = async () => {
                try {
                    const response = await axiosInstance.get<Account[]>('/accounts'); // Uses existing backend endpoint
                    setAccounts(response.data);
                    if (response.data.length > 0) {
                        setSelectedAccountId(response.data[0]._id); // Select the first account by default
                    }
                } catch (error) {
                    console.error('Failed to fetch accounts:', error);
                    showToast('Failed to load accounts for reporting.', 'error');
                }
            };
            fetchAccounts();
        }
    }, [isSuperAdmin, showToast]); // Dependencies for useEffect


    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-3xl font-semibold text-gray-800 mb-6">Reports</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* All Members Report Card */}
<div className="bg-gray-50 p-6 rounded-lg shadow-md border border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-700 mb-3">All Members List Report</h3>
                    <p className="text-gray-600 mb-4">Generate a list of all registered members (access restricted by your backend policies).</p>
                    {/* START MODIFICATION FOR ALL MEMBERS REPORT BUTTON */}
                    <div className="mb-4">
                        <label htmlFor="all-members-format-select" className="block text-sm font-medium text-gray-700 mb-1">Select Format:</label>
                        <select
                            id="all-members-format-select"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            value={allMembersReportFormat}
                            onChange={(e) => setAllMembersReportFormat(e.target.value as 'csv' | 'pdf')}
                            disabled={loadingAllMembers}
                        >
                            <option value="csv">CSV</option>
                            <option value="pdf">PDF</option>
                        </select>
                    </div>
                    <button
                        onClick={() => handleGenerateAllMembersReport(allMembersReportFormat)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loadingAllMembers}
                    >
                        {loadingAllMembers ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Generating...
                            </>
                        ) : (
                            `Generate All Members ${allMembersReportFormat.toUpperCase()}`
                        )}
                    </button>
                    {/* END MODIFICATION FOR ALL MEMBERS REPORT BUTTON */}
                </div>

                {/* NEW: Financial Records Report by Account and Admin (Super-Admin Only) */}
                {isSuperAdmin && (
                    <div className="bg-gray-50 p-6 rounded-lg shadow-md border border-gray-200">
                        <h3 className="text-xl font-semibold text-gray-700 mb-3">Financial Records by Account (Admin Recorded)</h3>
                        <p className="text-gray-600 mb-4">Generate financial records for a selected church account, specifically those recorded by 'admin' users.</p>
                        <div className="mb-4">
                            <label htmlFor="account-select" className="block text-sm font-medium text-gray-700 mb-1">Select Account:</label>
                            <select
                                id="account-select"
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                value={selectedAccountId}
                                onChange={(e) => {
                                    const newAccountId = e.target.value;
                                    setSelectedAccountId(newAccountId);
                                    const selectedAcc = accounts.find(acc => acc._id === newAccountId);
                                    setSelectedAccountName(selectedAcc ? selectedAcc.name : '');
                                }}
                                disabled={accounts.length === 0 || loadingFinancialRecords}
                            >
                                {accounts.length === 0 ? (
                                    <option value="">No accounts available</option>
                                ) : (
                                    accounts.map(account => (
                                        <option key={account._id} value={account._id}>
                                            {account.name}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                         <div className="mb-4">
                            <label htmlFor="financial-format-select" className="block text-sm font-medium text-gray-700 mb-1">Select Format:</label>
                            <select
                                id="financial-format-select"
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                value={financialReportFormat}
                                onChange={(e) => setFinancialReportFormat(e.target.value as 'csv' | 'pdf')}
                                disabled={loadingFinancialRecords}
                            >
                                <option value="csv">CSV</option>
                                <option value="pdf">PDF</option>
                            </select>
                        </div>
                        <button
                            onClick={() => handleGenerateAccountFinancialReport(financialReportFormat, selectedAccountId, selectedAccountName)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loadingFinancialRecords || !selectedAccountId}
                        >
                            {loadingFinancialRecords ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Generating...
                                </>
                            ) : (
                                `Generate Financial ${financialReportFormat.toUpperCase()}`
                            )}
                        </button>
                    </div>
                )}
                {/* Add other report types here */}
                {/* Example: General Financial Summary (Coming Soon) - can be visible to admin/super-admin */}
                {/* This section (Financial Summary (Coming Soon)) will now only show if not a Super Admin, or if you want it duplicated */}
                { !isSuperAdmin && (
                    <div className="bg-gray-50 p-6 rounded-lg shadow-md border border-gray-200">
                        <h3 className="text-xl font-semibold text-gray-700 mb-3">Financial Summary (Coming Soon)</h3>
                        <p className="text-gray-600 mb-4">A summarized report of income and expenses.</p>
                        <button
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-400 bg-gray-200 cursor-not-allowed"
                            disabled
                        >
                            Coming Soon
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportsPage;
